import {
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  useTheme,
  Rating,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import FormSmallTextField from "../../../../../../views/components/common/form/FormTextField";
import ImageUpload from "../../../../../../views/components/common/upload/ImageUpload";
import { ImageData } from "../../../../../../models/Image/Image";
import { useNotification } from "../../../../../../utils/NotificationsContext";
import { useRef, useEffect, useState } from "react";
import ImageAPI from "../../../../api/ImageAPI";
import VideoAPI from "../../../../api/VideoAPI";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import {
  DepartmentRating,
  FeedbackRatings,
  FeedbackFormFields,
  FeedbackSubmissionPayload,
  ExistingRatingForPrefill,
  ProjectFeedbackType,
} from "../../../../../../models/OpsSync/OpsSyncFeedbackForm";

interface ArchiveOrderPopupProps {
  departmentList: string[];
  open: boolean;
  onClose: () => void;
  selectedOrderId: number | null;
  orderCustomerName?: string;
  onSaveFeedback: (payload: FeedbackSubmissionPayload) => void;
  feedbackOnly?: boolean;
  existingRatings?: ExistingRatingForPrefill[];
  departmentIdToName?: Record<number, string>;
  inlineSuccessMessage?: string;
  onDismissInlineSuccess?: () => void;
}

const buildInitialRatings = (departments: string[]): FeedbackRatings => ({
  overallRating: 0,
  departmentRatings: departments.map((d) => ({ department: d, rating: 0 })),
});

const buildInitialTabFields = (): FeedbackFormFields => ({
  comment: "",
  lossIncurred: "",
  files: [],
});

const ArchiveOrderPopup: React.FC<ArchiveOrderPopupProps> = ({
  departmentList,
  open,
  onClose,
  selectedOrderId,
  orderCustomerName,
  onSaveFeedback,
  feedbackOnly = false,
  existingRatings = [],
  departmentIdToName = {},
  inlineSuccessMessage,
  onDismissInlineSuccess,
}) => {
  const theme = useTheme();
  const { showError } = useNotification();
  const dialogContentRef = useRef<HTMLDivElement>(null);

  const [activeTab, setActiveTab] = useState<"team" | "client">("team");
  const [savedTabs, setSavedTabs] = useState({ team: false, client: false });

  // Per-tab: only ratings
  const [teamRatings, setTeamRatings] = useState<FeedbackRatings>(() =>
    buildInitialRatings(departmentList),
  );
  const [clientRatings, setClientRatings] = useState<FeedbackRatings>(() =>
    buildInitialRatings(departmentList),
  );

  const [uploadingTab, setUploadingTab] = useState<"team" | "client" | null>(
    null,
  );

  const [teamFields, setTeamFields] = useState<FeedbackFormFields>(
    buildInitialTabFields,
  );
  const [clientFields, setClientFields] = useState<FeedbackFormFields>(
    buildInitialTabFields,
  );

  const currentFields = activeTab === "team" ? teamFields : clientFields;
  const setCurrentFields =
    activeTab === "team" ? setTeamFields : setClientFields;

  useEffect(() => {
    if (!open) {
      setActiveTab("team");
      setSavedTabs({ team: false, client: false });
      setUploadingTab(null);
      setTeamRatings(buildInitialRatings(departmentList));
      setClientRatings(buildInitialRatings(departmentList));
      setTeamFields(buildInitialTabFields());
      setClientFields(buildInitialTabFields());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (
      !open ||
      existingRatings.length === 0 ||
      Object.keys(departmentIdToName).length === 0
    ) {
      return;
    }

    const buildTabRatings = (r: ExistingRatingForPrefill): FeedbackRatings => {
      const overall = r.overall_rating ?? r.rating ?? 0;
      const deptList = r.departments ?? r.feedbacks ?? [];
      const departmentRatings: DepartmentRating[] = departmentList.map(
        (deptName) => {
          const deptEntry = Object.entries(departmentIdToName).find(
            ([, name]) => name === deptName,
          );
          const deptId = deptEntry ? Number(deptEntry[0]) : null;
          const match = deptList.find((d) => d.department_id === deptId);
          return {
            department: deptName,
            rating: match && "rating" in match ? (match.rating ?? 0) : 0,
          };
        },
      );
      return { overallRating: overall, departmentRatings };
    };

    let teamPre: FeedbackRatings | null = null;
    let clientPre: FeedbackRatings | null = null;
    let teamFieldsPre: FeedbackFormFields | null = null;
    let clientFieldsPre: FeedbackFormFields | null = null;

    existingRatings.forEach((r) => {
      const tabRatings = buildTabRatings(r);
      const fields: FeedbackFormFields = {
        comment: r.feedback ?? "",
        lossIncurred: String(r.cost_incurred ?? ""),
        files: [],
      };
      if (r.type === ProjectFeedbackType.TEAM) {
        teamPre = tabRatings;
        teamFieldsPre = fields;
      } else if (r.type === ProjectFeedbackType.CLIENT) {
        clientPre = tabRatings;
        clientFieldsPre = fields;
      }
    });

    if (teamPre) {
      setTeamRatings(teamPre);
      setSavedTabs((prev) => ({ ...prev, team: true }));
    }
    if (clientPre) {
      setClientRatings(clientPre);
      setSavedTabs((prev) => ({ ...prev, client: true }));
    }
    if (teamFieldsPre) setTeamFields(teamFieldsPre);
    if (clientFieldsPre) setClientFields(clientFieldsPre);
    if (teamPre && !clientPre) {
      setActiveTab("client");
    } else if (clientPre && !teamPre) {
      setActiveTab("team");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingRatings, departmentIdToName, departmentList]);

  useEffect(() => {
    const sync = (prev: FeedbackRatings): FeedbackRatings => {
      const existing = new Map(
        prev.departmentRatings.map((dr) => [dr.department, dr.rating]),
      );
      return {
        overallRating: prev.overallRating,
        departmentRatings: departmentList.map((d) => ({
          department: d,
          rating: existing.get(d) ?? 0,
        })),
      };
    };
    setTeamRatings((p) => sync(p));
    setClientRatings((p) => sync(p));
  }, [departmentList]);

  useEffect(() => {
    if (dialogContentRef.current) {
      dialogContentRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  useEffect(() => {
    if (!inlineSuccessMessage || !onDismissInlineSuccess) return;
    const timer = setTimeout(() => {
      onDismissInlineSuccess();
    }, 5000);
    return () => clearTimeout(timer);
  }, [inlineSuccessMessage, onDismissInlineSuccess]);

  const currentRatings = activeTab === "team" ? teamRatings : clientRatings;
  const setCurrentRatings =
    activeTab === "team" ? setTeamRatings : setClientRatings;
  const isSaved = savedTabs[activeTab];
  const isUploading = uploadingTab === activeTab;

  const setDepartmentRating = (department: string, value: number) => {
    setCurrentRatings((prev) => ({
      ...prev,
      departmentRatings: prev.departmentRatings.map((dr) =>
        dr.department === department ? { ...dr, rating: value } : dr,
      ),
    }));
  };

  const addFiles = (files: File[]) =>
    setCurrentFields((prev) => ({ ...prev, files: [...prev.files, ...files] }));

  const removeFile = (image: ImageData) =>
    setCurrentFields((prev) => ({
      ...prev,
      files: prev.files.filter((f) => f.name !== image.title),
    }));

  const validateCurrentTab = (): boolean => {
    if (currentRatings.overallRating < 1) {
      showError("Validation Error", "Please provide an overall rating.");
      return false;
    }

    const unrated = currentRatings.departmentRatings.filter(
      (dr) => dr.rating < 1,
    );
    if (unrated.length > 0) {
      showError(
        "Validation Error",
        `Please rate all departments. Missing: ${unrated.map((d) => d.department).join(", ")}.`,
      );
      return false;
    }

    if (currentFields.lossIncurred.trim() !== "") {
      const num = Number(currentFields.lossIncurred);
      if (isNaN(num) || num < 0) {
        showError(
          "Validation Error",
          "Loss Incurred must be a valid non-negative number.",
        );
        return false;
      }
    }

    return true;
  };

  const handleSaveTab = (archiveAfter: boolean) => {
    if (!validateCurrentTab()) return;

    const tabBeingSaved = activeTab;
    const ratingsToSave = activeTab === "team" ? teamRatings : clientRatings;
    const fieldsForTab = activeTab === "team" ? teamFields : clientFields;

    setUploadingTab(activeTab);
    const imageFiles = fieldsForTab.files.filter(
      (f) => f instanceof File && f.type.startsWith("image/"),
    ) as File[];
    const videoFiles = fieldsForTab.files.filter(
      (f) => f instanceof File && f.type.startsWith("video/"),
    ) as File[];

    const uploadImage =
      imageFiles.length > 0
        ? new ImageAPI().uploadImage(imageFiles, "product-images")
        : Promise.resolve<string[]>([]);

    const uploadVideo =
      videoFiles.length > 0
        ? new VideoAPI().uploadVideo(videoFiles, "projects-feedback")
        : Promise.resolve<string[]>([]);

    Promise.all([uploadImage, uploadVideo])
      .then(([imageUrls, videoUrls]) => {
        const allUrls = [...imageUrls, ...videoUrls];

        const comment = fieldsForTab.comment;
        const lossIncurred = fieldsForTab.lossIncurred;
        const fileUrls: string[] =
          allUrls.length > 0
            ? allUrls
            : (fieldsForTab.files.filter(
                (f) => typeof f === "string",
              ) as unknown as string[]);

        if (tabBeingSaved === "team") {
          setTeamFields((prev) => ({ ...prev, files: [] }));
        } else {
          setClientFields((prev) => ({ ...prev, files: [] }));
        }
        setSavedTabs((prev) => ({ ...prev, [tabBeingSaved]: true }));

        if (!archiveAfter) {
          setActiveTab(tabBeingSaved === "team" ? "client" : "team");
        }

        const isClientTab = tabBeingSaved === "client";
        const shouldClose = feedbackOnly && isClientTab;

        onSaveFeedback({
          type:
            tabBeingSaved === "team"
              ? ProjectFeedbackType.TEAM
              : ProjectFeedbackType.CLIENT,
          overallRating: ratingsToSave.overallRating,
          departmentRatings: ratingsToSave.departmentRatings,
          comment,
          lossIncurred,
          files: fileUrls,
          archiveAfter,
          shouldClose,
        });
      })
      .catch((error) => {
        showError(
          "Upload Error",
          error?.response?.data?.message ||
            "File upload failed. Please try again.",
        );
      })
      .finally(() => setUploadingTab(null));
  };

  const purple = theme?.palette?.customColors?.purple || "#9C27B0";
  const green = theme?.palette?.mintGreen?.main || "#4CAF50";
  const grey = theme?.palette?.customColors?.greyTextColor || "#9E9E9E";
  const pinkDark = theme?.palette?.customColors?.pinkDark || "#E91E63";
  const isDark = theme.palette.mode === "dark";

  const starSx = {
    "& .MuiRating-iconFilled": { color: purple },
    "& .MuiRating-iconHover": { color: purple },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
          },
        },
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${purple}18 0%, ${purple}06 100%)`,
          borderBottom: `1px solid ${purple}22`,
          px: 2.5,
          pt: 2,
          pb: 0,
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
            "&:hover": {
              bgcolor: isDark ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.10)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>

        <Box display="flex" alignItems="center" gap={1} mb={1} pr={4}>
          <StarRoundedIcon
            sx={{ color: purple, fontSize: 20, flexShrink: 0 }}
          />
          <Box>
            <CustomTypography
              variant="heading5"
              style={{ marginBottom: "5px" }}
            >
              Project Feedback
            </CustomTypography>
            <CustomTypography variant="heading9" color={grey}>
              #{selectedOrderId}
              {orderCustomerName ? ` · ${orderCustomerName}` : ""}
            </CustomTypography>
          </Box>
        </Box>

        <Box display="flex">
          {[
            {
              key: "team" as const,
              step: 1,
              label: "Team Experience",
              icon: <GroupsOutlinedIcon sx={{ fontSize: 15 }} />,
            },
            {
              key: "client" as const,
              step: 2,
              label: "Client Experience",
              icon: <PersonOutlineOutlinedIcon sx={{ fontSize: 15 }} />,
            },
          ].map((tab) => {
            const isActive = activeTab === tab.key;
            const isSavedTab = savedTabs[tab.key];
            const isClientTabLocked =
              !feedbackOnly && tab.key === "client" && !savedTabs.team;
            const isClickable = !isClientTabLocked;
            return (
              <Box
                key={tab.key}
                onClick={() => isClickable && setActiveTab(tab.key)}
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                  py: 1.25,
                  cursor: isClickable ? "pointer" : "not-allowed",
                  borderBottom: isActive
                    ? `2.5px solid ${purple}`
                    : "2.5px solid transparent",
                  color: isActive ? purple : isClientTabLocked ? grey : grey,
                  fontWeight: isActive ? 700 : 500,
                  fontSize: "0.82rem",
                  transition: "all 0.18s",
                  userSelect: "none",
                  opacity: isClientTabLocked ? 0.6 : 1,
                  "&:hover": isClickable
                    ? { color: purple, bgcolor: `${purple}08` }
                    : {},
                }}
              >
                {tab.icon}
                {tab.step}. {tab.label}
                {isSavedTab && (
                  <CheckCircleIcon sx={{ fontSize: 13, color: green }} />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      <DialogContent
        ref={dialogContentRef}
        sx={{ px: 3, py: 2.5, scrollbarWidth: "thin", position: "relative" }}
      >
        {inlineSuccessMessage && onDismissInlineSuccess && (
          <Box
            component="button"
            type="button"
            onClick={() => onDismissInlineSuccess()}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              py: 1.5,
              px: 2,
              mb: 2,
              width: "100%",
              textAlign: "left",
              borderRadius: "10px",
              backgroundColor: `${green}18`,
              border: `1px solid ${green}40`,
              cursor: "pointer",
              font: "inherit",
              "&:hover": { backgroundColor: `${green}22` },
            }}
          >
            <CheckCircleIcon sx={{ color: green, fontSize: 22 }} />
            <CustomTypography variant="body2" fontWeight={600} color={green}>
              {inlineSuccessMessage}
            </CustomTypography>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2.5,
            px: 2,
            py: 1.25,
            borderRadius: "10px",
            border: `1px solid ${
              currentRatings.overallRating > 0
                ? `${purple}30`
                : isDark
                  ? "rgba(255,255,255,0.09)"
                  : "rgba(0,0,0,0.08)"
            }`,
            bgcolor:
              currentRatings.overallRating > 0
                ? `${purple}08`
                : isDark
                  ? "rgba(255,255,255,0.03)"
                  : "rgba(0,0,0,0.02)",
            transition: "all 0.2s",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            {currentRatings.overallRating > 0 ? (
              <CheckCircleIcon sx={{ fontSize: 14, color: green }} />
            ) : (
              <StarBorderRoundedIcon sx={{ fontSize: 14, color: grey }} />
            )}
            <CustomTypography variant="heading6" fontWeight={700}>
              Overall Rating
            </CustomTypography>
          </Box>
          <Rating
            value={currentRatings.overallRating}
            size="large"
            onChange={(_, val) =>
              setCurrentRatings((prev) => ({
                ...prev,
                overallRating: val ?? 0,
              }))
            }
            sx={starSx}
          />
        </Box>
        <Box sx={{ mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
          >
            <CustomTypography variant="heading6" fontWeight={700}>
              Department Ratings
            </CustomTypography>
            <Chip
              label="Required"
              size="small"
              sx={{
                height: 20,
                fontSize: "0.6rem",
                fontWeight: 700,
                bgcolor: `${theme?.palette?.error?.main}14`,
                color: theme?.palette?.error?.main,
                border: `1px solid ${theme?.palette?.error?.main}30`,
              }}
            />
          </Box>

          {currentRatings.departmentRatings.length === 0 ? (
            <CustomTypography variant="heading9" color={grey}>
              No departments configured.
            </CustomTypography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {currentRatings.departmentRatings.map((dr) => (
                <Box
                  key={dr.department}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    py: 1.25,
                    borderRadius: "10px",
                    border: `1px solid ${
                      dr.rating > 0
                        ? `${purple}30`
                        : isDark
                          ? "rgba(255,255,255,0.09)"
                          : "rgba(0,0,0,0.08)"
                    }`,
                    bgcolor:
                      dr.rating > 0
                        ? `${purple}08`
                        : isDark
                          ? "rgba(255,255,255,0.03)"
                          : "rgba(0,0,0,0.02)",
                    transition: "all 0.2s",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    {dr.rating > 0 ? (
                      <CheckCircleIcon sx={{ fontSize: 14, color: green }} />
                    ) : (
                      <StarBorderRoundedIcon
                        sx={{ fontSize: 14, color: grey }}
                      />
                    )}
                    <CustomTypography variant="heading8">
                      {dr.department}
                    </CustomTypography>
                  </Box>
                  <Rating
                    value={dr.rating}
                    onChange={(_, val) =>
                      setDepartmentRating(dr.department, val ?? 0)
                    }
                    size="small"
                    sx={starSx}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <CustomTypography variant="heading6" fontWeight={700}>
              Feedback Comment
            </CustomTypography>
            <CustomTypography
              variant="caption"
              color={
                currentFields.comment.length > 280
                  ? theme?.palette?.error?.main
                  : grey
              }
            >
              {currentFields.comment.length}/300 · Optional
            </CustomTypography>
          </Box>
          <FormSmallTextField
            label="Share your feedback here..."
            multiline
            rows={3}
            variant="outlined"
            color="secondary"
            type="name"
            fontSize="14px"
            value={currentFields.comment}
            onChange={(_id, value) =>
              setCurrentFields((prev) => ({
                ...prev,
                comment: (value || "").slice(0, 300),
              }))
            }
            sx={{ marginBottom: 0 }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Box display="flex" alignItems="center" gap={0.75}>
              <CustomTypography variant="heading6" fontWeight={700}>
                Loss Incurred
              </CustomTypography>
            </Box>
            <CustomTypography variant="caption" color={grey}>
              Optional
            </CustomTypography>
          </Box>
          <CustomTextField
            placeholder="Enter loss incurred value..."
            allowNegativeValue={false}
            type="number"
            value={currentFields.lossIncurred}
            onChange={(event) =>
              setCurrentFields((prev) => ({
                ...prev,
                lossIncurred: event.target.value,
              }))
            }
            width="100%"
          />
        </Box>

        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
          >
            <CustomTypography variant="heading6" fontWeight={700}>
              Attachments
            </CustomTypography>
            <CustomTypography variant="caption" color={grey}>
              Images &amp; videos · Optional
            </CustomTypography>
          </Box>
          <ImageUpload
            key={`archive-feedback-upload-${activeTab}`}
            uploadText="Drop files here, or browse"
            minHeight="110px"
            id={`archive-feedback-upload-${activeTab}`}
            showImagesList={true}
            size="large"
            isViewOnly={false}
            existingImageList={[]}
            newImageList={currentFields.files}
            deleteImage={() => {}}
            removeImage={(image: ImageData) => removeFile(image)}
            addImagesToBeUploaded={(files: File[]) => addFiles(files)}
            allowedFileTypes="image/*,video/*"
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 1.5,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
          borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)"}`,
          "& > :not(style) ~ :not(style)": { marginLeft: 0 },
        }}
      >
        <AppButton
          onClick={onClose}
          label="Cancel"
          customStyles={{
            height: "2.25rem",
            minWidth: "8rem",
            border: `1px solid ${pinkDark}`,
            backgroundColor: "transparent",
            color: pinkDark,
            "&:hover": { bgcolor: `${pinkDark}10` },
          }}
          variant="grayscale"
        />

        {(() => {
          const isContinueMode =
            activeTab === "team" && !savedTabs.team && !savedTabs.client;
          const archiveAfter = feedbackOnly ? false : !isContinueMode;
          return (
            <AppButton
              onClick={() => handleSaveTab(archiveAfter)}
              disabled={isUploading || isSaved}
              label={
                isUploading ? (
                  "Uploading..."
                ) : feedbackOnly ? (
                  "Save"
                ) : isContinueMode ? (
                  "Save & Continue"
                ) : (
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <CheckCircleIcon sx={{ fontSize: 16 }} />
                    Save &amp; Archive
                  </Box>
                )
              }
              customStyles={{
                height: "2.25rem",
                minWidth: "10rem",
                background: `linear-gradient(135deg, ${purple}, ${purple}cc)`,
                color: "white",
                boxShadow: `0 3px 12px ${purple}44`,
                opacity: isUploading || isSaved ? 0.7 : 1,
              }}
              variant="primary"
            />
          );
        })()}
      </DialogActions>
    </Dialog>
  );
};

export default ArchiveOrderPopup;
